import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, UserPlus, ArrowLeft, ShieldCheck, KeyRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

const inputClass =
  'h-10 border-[rgba(6, 43, 26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212, 175, 55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

function genPassword() {
  const charset =
    'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%';
  let out = '';
  for (let i = 0; i < 14; i++) {
    out += charset[Math.floor(Math.random() * charset.length)];
  }
  return out;
}

export default function CreateInstructor() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: genPassword(),
    department: '',
    bio: '',
  });
  const [showPassword, setShowPassword] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    if (!form.fullName.trim()) return 'Please enter the instructor full name';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/admin/instructors', {
        email: form.email.trim(),
        password: form.password,
        full_name: form.fullName.trim(),
        department: form.department.trim(),
        bio: form.bio.trim(),
      });
      toast.success(`Instructor ${form.fullName} created`);
      navigate('/admin/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || err?.message || 'Failed to create instructor');
    } finally {
      setSubmitting(false);
    }
  };

  const regeneratePassword = () => {
    setForm((f) => ({ ...f, password: genPassword() }));
    setShowPassword(true);
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(form.password);
      toast.success('Password copied to clipboard');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="create-instructor-page">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-[#062B1A]/60 hover:text-[#062B1A]"
          data-testid="create-instructor-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2
            className="text-2xl font-semibold text-[#062B1A]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Add Instructor
          </h2>
          <p className="text-sm text-[#062B1A]/60 mt-0.5">
            Provision a new instructor account and login credentials.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-[0_1px_0_rgba(0,0,0,0.04),0_10px_30px_rgba(6, 43, 26,0.06)]">
            <CardHeader className="border-b border-[rgba(6, 43, 26,0.08)]">
              <CardTitle
                className="text-base font-semibold text-[#062B1A]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Instructor details
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="create-instructor-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-sm font-medium text-[#062B1A]">
                      Full name
                    </Label>
                    <Input
                      id="fullName"
                      value={form.fullName}
                      onChange={update('fullName')}
                      placeholder="Dr. Jane Doe"
                      data-testid="instructor-fullname"
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-[#062B1A]">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={update('email')}
                      placeholder="instructor@school.edu"
                      data-testid="instructor-email"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department" className="text-sm font-medium text-[#062B1A]">
                    Department <span className="text-[#062B1A]/40 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="department"
                    value={form.department}
                    onChange={update('department')}
                    placeholder="Computer Science"
                    data-testid="instructor-department"
                    className={inputClass}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio" className="text-sm font-medium text-[#062B1A]">
                    Short bio <span className="text-[#062B1A]/40 font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    id="bio"
                    rows={3}
                    value={form.bio}
                    onChange={update('bio')}
                    placeholder="Brief background, specialties, etc."
                    data-testid="instructor-bio"
                    className="border-[rgba(6, 43, 26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212, 175, 55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium text-[#062B1A]">
                      Initial password
                    </Label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={regeneratePassword}
                        className="text-xs font-medium text-[#062B1A]/60 hover:text-[#D4AF37] transition-colors"
                        data-testid="instructor-password-regenerate"
                      >
                        Regenerate
                      </button>
                      <span className="text-[#062B1A]/20">·</span>
                      <button
                        type="button"
                        onClick={copyPassword}
                        className="text-xs font-medium text-[#062B1A]/60 hover:text-[#D4AF37] transition-colors"
                        data-testid="instructor-password-copy"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#062B1A]/30" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={update('password')}
                      data-testid="instructor-password"
                      className={`${inputClass} pl-9 pr-10 font-mono`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#062B1A]/40 hover:text-[#062B1A]"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-[11px] text-[#062B1A]/50">
                    The instructor can change this after their first sign in.
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="create-instructor-submit"
                    className="bg-[#062B1A] text-white hover:bg-[#041B10] focus-visible:ring-4 focus-visible:ring-[rgba(212, 175, 55,0.35)] h-10"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating…
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create instructor
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    className="border-[rgba(6, 43, 26,0.16)] text-[#062B1A] hover:bg-[#F7F8FA] h-10"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="border border-[rgba(212, 175, 55,0.4)] bg-[rgba(212, 175, 55,0.04)]">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#062B1A]">
                  Admin only
                </span>
              </div>
              <p className="text-sm text-[#062B1A]/75 leading-relaxed">
                Instructor accounts cannot self-register. You're creating both the
                authentication record and the MCC Portal instructor profile in one step.
              </p>
            </CardContent>
          </Card>

          <Card className="border border-[rgba(6, 43, 26,0.08)]">
            <CardContent className="p-5 space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#062B1A]/50">
                What happens next
              </p>
              <ol className="space-y-2.5 text-sm text-[#062B1A]/75">
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#062B1A] text-[#D4AF37] text-[10px] font-semibold flex items-center justify-center mt-0.5">
                    1
                  </span>
                  <span>An auth account is created in the MCC Portal users collection (no email verification needed).</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#062B1A] text-[#D4AF37] text-[10px] font-semibold flex items-center justify-center mt-0.5">
                    2
                  </span>
                  <span>An instructor record is created in the MCC Portal database.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#062B1A] text-[#D4AF37] text-[10px] font-semibold flex items-center justify-center mt-0.5">
                    3
                  </span>
                  <span>Share the email + password with them — they can sign in immediately.</span>
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
